import { useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';
import { EllipsisVertical, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@nesso/ui/primitives/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@nesso/ui/primitives/dropdown-menu';

import {
    type Service,
    serviceCatalogOptions,
    useAddServiceOptions,
    useDeleteServiceOptions,
    useSetDefaultServiceOptions,
} from '@/entities/service-catalog';
import { useConfirmDialogStore } from '@/shared/stores/confirm-dialog-store';

type Props = {
    service: Service;
    onEdit: (service: Service) => void;
};

export function ServicesTableRowMenu({ service, onEdit }: Props) {
    // Reads the cache the page's own `useSuspenseQuery` already populated, the way the code
    // picker does, to find the current default's code when this row makes a different one default.
    const { data: catalog } = useQuery(serviceCatalogOptions());
    const setDefaultService = useMutation(useSetDefaultServiceOptions());
    const deleteService = useMutation(useDeleteServiceOptions());
    const addService = useMutation(useAddServiceOptions());
    const openConfirmDialog = useConfirmDialogStore((state) => state.open);
    const [open, setOpen] = useState(false);

    function onMakeDefault() {
        const previousDefaultCode = catalog?.services.find(
            (candidate) => candidate.isDefault,
        )?.code;

        setDefaultService.mutate(service.code, {
            onSuccess: () => {
                toast.success('Default service changed', {
                    icon: null,
                    action: {
                        label: 'Undo',
                        onClick: () => {
                            if (previousDefaultCode) setDefaultService.mutate(previousDefaultCode);
                        },
                    },
                });
            },
            onSettled: () => setOpen(false),
        });
    }

    function onDelete() {
        openConfirmDialog({
            title: 'Delete this service?',
            confirmText: 'Delete',
            // Solid Danger button, not the muted `destructive`.
            variant: 'danger',
            onConfirm: () => {
                deleteService.mutate(service.code, {
                    onSuccess: () => {
                        toast.success('Service deleted', {
                            icon: null,
                            action: {
                                label: 'Undo',
                                onClick: () =>
                                    addService.mutate({
                                        code: service.code,
                                        description: service.description,
                                        rateCents: service.rateCents,
                                        durationMinutes: service.durationMinutes,
                                        makeDefault: false,
                                    }),
                            },
                        });
                    },
                });
            },
        });
    }

    return (
        <DropdownMenu open={open} onOpenChange={setOpen}>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon-sm" aria-label="Service options">
                    <EllipsisVertical className="size-4" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-auto">
                {/* The entity's `onSuccess` awaits the list refetch, so `isDefault` can flip to
                    true before `isPending` turns false — keep the item mounted through that gap
                    so it isn't unmounted out from under its own spinner. */}
                {(!service.isDefault || setDefaultService.isPending) && (
                    <DropdownMenuItem
                        onSelect={(event) => {
                            event.preventDefault();
                            onMakeDefault();
                        }}
                        disabled={setDefaultService.isPending}
                    >
                        {setDefaultService.isPending && <Loader2 className="animate-spin" />}
                        Make default
                    </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => onEdit(service)}>Edit</DropdownMenuItem>
                {!service.isDefault && (
                    <DropdownMenuItem onClick={onDelete}>Delete</DropdownMenuItem>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
